import { useMutation, useMutationState } from '@tanstack/react-query';
import { isNonEmptyObject } from '@pkgs/components/utils/searchStateUtils';
import { mapSearchListingsFiltersToSearchState } from '@pkgs/components/utils/mapFilterFields';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import { DEFAULT_FILTERS } from '@pkgs/components/consts/searchDefaults';
import {
  isHttpError,
  mapSearchFilterRequestToSearchListingsFilters,
  mapSearchFilterRequestToSearchListingsOptions,
  type SearchListingsOptions,
} from '@ev-tech/search-modules-api';

import { AI_SEARCH_QUERY_KEY } from './queryKeys';
import { useShopFilterRemovalMessage } from '../useShopFilterRemovalMessage';
import { useIsLipaPage } from '../useIsLipaPage';
import { useAppContext } from '../useAppContext';
import { useAutoCompleteStore } from '../stores/useAutoCompleteStore';
import { useSearchStore } from '../stores/search/useSearchStore';
import { SearchMode } from '../stores/search/slices/searchModeSlice';

export type PromptOptions = Partial<Pick<SearchListingsOptions, 'currency' | 'measurementSystem'>>;

export const AI_SEARCH_TIMEOUT_MS = 10_000;

export const useAiSearch = () => {
  const { api, options, events } = useAppContext();

  const applyState = useSearchStore((state) => state.applyState);
  const setIsPreferencesUnavailable = useSearchStore((state) => state.setIsPreferencesUnavailable);
  const isLipaPage = useIsLipaPage();
  const { removeShopFilterWithUndoMessage } = useShopFilterRemovalMessage();

  const mutation = useMutation({
    mutationKey: [AI_SEARCH_QUERY_KEY],
    mutationFn: async (promptValue: string) => {
      const filtersResult = await api
        .withConfig({
          timeout: AI_SEARCH_TIMEOUT_MS,
        })
        .getFilterByAiPrompt({
          language: options.language,
          text: promptValue,
          marketCountryCode: options.marketCountryCode,
        });

      if (filtersResult.status === 'error') {
        if (isHttpError(filtersResult.error) && filtersResult.error.code === 'ETIMEDOUT') {
          events?.onError?.(filtersResult.error, {
            context: { message: 'AI search request timed out', statusCode: 408 },
          });
        } else {
          events?.onError?.(filtersResult.error, {
            context: { statusCode: filtersResult.statusCode, message: filtersResult.errorMessage },
          });
        }
        throw filtersResult.error;
      }

      if (!filtersResult.data.searchFilter) {
        events?.onError?.(new Error('No search filter found for the prompt text'), {
          context: { message: `No search filter found for the prompt text: '${promptValue}'` },
        });
      }

      const refinedFilters = filtersResult.data.searchFilter
        ? mapSearchFilterRequestToSearchListingsFilters(filtersResult.data.searchFilter)
        : DEFAULT_FILTERS;
      const hasPreferences = isNonEmptyObject(refinedFilters.preferences);

      let promptOptions: PromptOptions | undefined;
      if (filtersResult.data.searchFilter) {
        promptOptions = mapSearchFilterRequestToSearchListingsOptions(filtersResult.data.searchFilter);
      }

      const newState = {
        ...mapSearchListingsFiltersToSearchState({ filters: refinedFilters, placeName: filtersResult.data.placeName, options }),
        searchMode: SearchMode.AI,
        modalSearchMode: SearchMode.AI,
        ...(hasPreferences && { sortingOptionId: SortingOptionId.PREFERENCES }),
      };

      return { newState, preferencesUnavailable: filtersResult.data.preferencesUnavailable, promptOptions };
    },
    onSuccess: ({ newState, preferencesUnavailable }) => {
      if (isLipaPage) {
        removeShopFilterWithUndoMessage();
      }
      applyState({ state: newState });
      useAutoCompleteStore.getState().resetAiDraft?.();
      setIsPreferencesUnavailable(preferencesUnavailable);
    },
  });

  // Get the latest mutation state for the AI search query
  const latestMutationState = useMutationState({
    filters: { mutationKey: [AI_SEARCH_QUERY_KEY] },
    select: (mutation) => ({
      isPending: mutation.state.status === 'pending',
      isError: mutation.state.status === 'error',
      isSuccess: mutation.state.status === 'success',
      promptOptions: (mutation.state.data as { promptOptions?: PromptOptions } | undefined)?.promptOptions,
    }),
  }).at(-1) || { isPending: false, isError: false, isSuccess: false, promptOptions: undefined };

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: latestMutationState.isPending,
    isError: latestMutationState.isError,
    isSuccess: latestMutationState.isSuccess,
    promptOptions: latestMutationState.promptOptions,
  };
};
